import * as THREE from 'three';

export interface RendererShadowOptions {
  readonly enabled?: boolean;
  readonly type?: THREE.ShadowMapType;
  readonly autoUpdate?: boolean;
}

export interface RendererOptions {
  readonly antialias?: boolean;
  readonly alpha?: boolean;
  readonly powerPreference?: WebGLPowerPreference;
  readonly exposure?: number;
  readonly toneMapping?: THREE.ToneMapping;
  readonly shadows?: boolean | RendererShadowOptions;
}

export interface RendererDiagnostics {
  readonly backend: 'webgl';
  readonly outputColorSpace: string;
  readonly toneMapping: string;
  readonly exposure: number;
  readonly pixelRatio: number;
  readonly cssWidth: number;
  readonly cssHeight: number;
  readonly bufferWidth: number;
  readonly bufferHeight: number;
  readonly shadows: Readonly<{
    enabled: boolean;
    type: string;
    autoUpdate: boolean;
  }>;
  readonly render: Readonly<{
    frame: number;
    calls: number;
    triangles: number;
    points: number;
    lines: number;
  }>;
  readonly memory: Readonly<{
    geometries: number;
    textures: number;
    programs: number;
  }>;
}

export const DEFAULT_RENDERER_EXPOSURE = 1.05;
export const DEFAULT_RENDERER_MAX_DPR = 2;

/** Creates the single color-management and shadow authority for the game. */
export function createRenderer(
  canvas: HTMLCanvasElement,
  options: RendererOptions = {},
): THREE.WebGLRenderer {
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: options.antialias ?? true,
    alpha: options.alpha ?? false,
    powerPreference: options.powerPreference ?? 'high-performance',
  });
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = options.toneMapping ?? THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = readPositiveFinite(
    options.exposure ?? DEFAULT_RENDERER_EXPOSURE,
    'exposure',
  );
  renderer.autoClear = true;

  const shadows = normalizeShadowOptions(options.shadows);
  renderer.shadowMap.enabled = shadows.enabled;
  renderer.shadowMap.type = shadows.type;
  renderer.shadowMap.autoUpdate = shadows.autoUpdate;
  renderer.shadowMap.needsUpdate = shadows.enabled;
  return renderer;
}

/**
 * Resizes the renderer drawing buffer only when its effective dimensions
 * change. The DPR cap protects high-density desktop displays from quadratic
 * fill-rate growth.
 */
export function resizeRenderer(
  renderer: THREE.WebGLRenderer,
  camera: THREE.PerspectiveCamera,
  maxDpr = DEFAULT_RENDERER_MAX_DPR,
): boolean {
  const canvas = renderer.domElement;
  const width = Math.max(1, Math.floor(canvas.clientWidth));
  const height = Math.max(1, Math.floor(canvas.clientHeight));
  const dpr = getCappedDevicePixelRatio(maxDpr);
  const bufferWidth = Math.floor(width * dpr);
  const bufferHeight = Math.floor(height * dpr);
  const needsResize =
    canvas.width !== bufferWidth ||
    canvas.height !== bufferHeight ||
    renderer.getPixelRatio() !== dpr;

  if (needsResize) {
    renderer.setPixelRatio(dpr);
    renderer.setSize(width, height, false);
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
  }

  return needsResize;
}

export function getCappedDevicePixelRatio(maxDpr = DEFAULT_RENDERER_MAX_DPR): number {
  const cap = readPositiveFinite(maxDpr, 'maxDpr');
  const devicePixelRatio = typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1;
  return Math.max(0.5, Math.min(devicePixelRatio, cap));
}

/** Fresh immutable diagnostics suitable for the game's debug snapshot. */
export function getRendererDiagnostics(renderer: THREE.WebGLRenderer): RendererDiagnostics {
  const canvas = renderer.domElement;
  const drawingBuffer = renderer.getDrawingBufferSize(new THREE.Vector2());
  const info = renderer.info;
  return Object.freeze({
    backend: 'webgl' as const,
    outputColorSpace: renderer.outputColorSpace,
    toneMapping: describeToneMapping(renderer.toneMapping),
    exposure: renderer.toneMappingExposure,
    pixelRatio: renderer.getPixelRatio(),
    cssWidth: Math.max(1, Math.floor(canvas.clientWidth)),
    cssHeight: Math.max(1, Math.floor(canvas.clientHeight)),
    bufferWidth: drawingBuffer.x,
    bufferHeight: drawingBuffer.y,
    shadows: Object.freeze({
      enabled: renderer.shadowMap.enabled,
      type: describeShadowMapType(renderer.shadowMap.type),
      autoUpdate: renderer.shadowMap.autoUpdate,
    }),
    render: Object.freeze({
      frame: info.render.frame,
      calls: info.render.calls,
      triangles: info.render.triangles,
      points: info.render.points,
      lines: info.render.lines,
    }),
    memory: Object.freeze({
      geometries: info.memory.geometries,
      textures: info.memory.textures,
      programs: info.programs?.length ?? 0,
    }),
  });
}

function normalizeShadowOptions(
  options: boolean | RendererShadowOptions | undefined,
): Required<RendererShadowOptions> {
  if (typeof options === 'boolean') {
    return {
      enabled: options,
      type: THREE.PCFSoftShadowMap,
      autoUpdate: true,
    };
  }
  return {
    enabled: options?.enabled ?? true,
    type: options?.type ?? THREE.PCFSoftShadowMap,
    autoUpdate: options?.autoUpdate ?? true,
  };
}

function describeToneMapping(value: THREE.ToneMapping): string {
  if (value === THREE.NoToneMapping) return 'none';
  if (value === THREE.LinearToneMapping) return 'linear';
  if (value === THREE.ReinhardToneMapping) return 'reinhard';
  if (value === THREE.CineonToneMapping) return 'cineon';
  if (value === THREE.ACESFilmicToneMapping) return 'aces-filmic';
  if (value === THREE.AgXToneMapping) return 'agx';
  if (value === THREE.NeutralToneMapping) return 'neutral';
  return `custom-${value}`;
}

function describeShadowMapType(value: THREE.ShadowMapType): string {
  if (value === THREE.BasicShadowMap) return 'basic';
  if (value === THREE.PCFShadowMap) return 'pcf';
  if (value === THREE.PCFSoftShadowMap) return 'pcf-soft';
  if (value === THREE.VSMShadowMap) return 'vsm';
  return `custom-${value}`;
}

function readPositiveFinite(value: number, label: string): number {
  if (!Number.isFinite(value) || value <= 0) {
    throw new RangeError(`${label} must be a finite number greater than zero.`);
  }
  return value;
}
